import type { Prismic } from '@landsbankinn/prismic';
import {
  fallbackImage,
  fallbackRichText,
  fallbackText,
  getDataFromLink,
  PrismicLink,
} from '@landsbankinn/prismic';
import { ArrowButton, Segment, useMedia } from '@landsbankinn/ui';
import React, { forwardRef, useContext, useRef } from 'react';
import { CardGroup } from '../components/card-group/CardGroup';
import { FeaturedGroup } from '../components/card-group/FeaturedGroup';
import { Card } from '../components/card/Card';
import { SplittedCard } from '../components/card/SplitedCard';
import { PageTransitionContext } from '../lib/page-transition';

interface IProps {
  featured: boolean;
  align: 'Left' | 'Right';
  four_in_row: boolean;
  fields: IField[];
}

interface IField {
  image: Prismic.Image;
  video?: Prismic.Embed;
  heading: Prismic.RichText;
  copy: Prismic.RichText;
  text_in_image?: boolean;
  link: Prismic.Link;
  isFeatured: boolean;
  isHorizontal: boolean;
  count: number;
  style?: any;
}

const SplittedItem = forwardRef(function CardItem(
  { image, heading, copy, link, isFeatured, style }: IField,
  ref,
) {
  const linkdata = getDataFromLink(link);
  const title = fallbackText(heading, linkdata.title ?? undefined);
  const imageRef = useRef();
  const isMobile = useMedia('sm');

  return (
    <SplittedCard
      link={link}
      imageRef={imageRef}
      title={title}
      description={fallbackRichText(copy, linkdata.abstract ?? undefined)}
      action={link && <ArrowButton hideText />}
      image={fallbackImage(image, linkdata.image ?? undefined)}
      padding={false}
      notLink={!link}
      ar={isFeatured ? 'ar_1_1' : isMobile ? 'ar_1_1' : 'ar_4_3'}
      imageAspect={isFeatured ? 1 / 1 : isMobile ? 1 / 1 : 4 / 3}
      style={style}
      isFeatured={isFeatured}
    />
  );
});

const CardItem = forwardRef(function CardItem(
  { image, heading, copy, link, count, video, ...rest }: IField,
  ref,
) {
  const linkdata = getDataFromLink(link);
  const title = fallbackText(heading, linkdata.title ?? undefined);
  const isMobile = useMedia('sm');

  return (
    <Card
      link={link}
      title={
        link ? (
          <PrismicLink value={link} title={title ?? undefined}>
            <a>{title}</a>
          </PrismicLink>
        ) : (
          title
        )
      }
      description={fallbackRichText(copy, linkdata.abstract ?? undefined)}
      image={fallbackImage(image, linkdata.image ?? undefined)}
      ar={count === 2 ? 'ar_1_1' : isMobile ? 'ar_1_1' : 'ar_4_3'}
      percentage={1 / Math.min(3, count)}
      padding={false}
      notLink={!link}
      moreBodySpace={count === 2}
      imageAspect={count === 2 ? 1 / 1 : isMobile ? 1 / 1 : 4 / 3}
      video={video}
      {...rest}
    />
  );
});

export const ProductCardsContainer = ({
  featured,
  align,
  fields,
  four_in_row,
}: IProps) => {
  if (!fields) {
    return null;
  }

  const count = fields.length;
  const colSize = four_in_row ? 4 : count <= 2 || count === 4 ? 2 : 3;

  // rendering featured layout left or right aligned
  if (featured === true && count === 3) {
    return (
      <Segment paddingBottomMobile={0}>
        <FeaturedGroup align={align}>
          {fields?.map(({ isFeatured, ...rest }, index) => {
            return (
              <SplittedItem
                key={`splitted-item-${index}`}
                isFeatured={featured && index === 0}
                {...rest}
              />
            );
          })}
        </FeaturedGroup>
      </Segment>
    );
  }

  return (
    <Segment paddingBottomMobile={0}>
      <CardGroup columns={colSize}>
        {fields?.map((item, index) => (
          <CardItem key={`product-item-${index}`} {...item} count={count} />
        ))}
      </CardGroup>
    </Segment>
  );
};
